import { useState, type ReactNode } from "react";
import {
  ChevronDownIcon,
  ClipboardListIcon,
  DocumentIcon,
  GlobeIcon,
  LightBulbIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PuzzlePieceIcon,
  ToolsIcon,
  UsersIcon,
  CheckIcon,
  XMarkIcon,
} from "@sortera/react-icons";
import type { ToolKind, ToolStep } from "../../types/index.ts";
import { Spinner } from "../../ui/spinner/index.ts";
import styles from "./activity-steps.module.css";

const ICONS: Record<ToolKind, typeof DocumentIcon> = {
  read: DocumentIcon,
  edit: PencilSquareIcon,
  search: MagnifyingGlassIcon,
  command: ToolsIcon,
  skill: LightBulbIcon,
  plan: ClipboardListIcon,
  agent: UsersIcon,
  web: GlobeIcon,
  other: PuzzlePieceIcon
}

function ToolIcon({kind, size = 16}: {kind: ToolKind; size?: number}) {
  const Icon = ICONS[kind];
  return <Icon size = {size} />;
}

/** A recipe or helper by its own name, without the plugin it came from: `build-feature`. */
function shortName(name: string): string {
  return name.split(":").at(-1) ?? name;
}

/** The recipes and helpers the turn used, each once, in the order they were first used. */
function highlights(steps: ToolStep[]): { kind: "skill" | "agent"; name: string }[] {
  const seen = new Map<string, { kind: "skill" | "agent"; name: string }>();
  for (const step of steps) {
    if ((step.kind === "skill" || step.kind === "agent") && step.detail) {
      // One step can open several recipes at once: "new-frontend, npm-auth".
      for (const part of step.detail.split(", ")) {
        const name = shortName(part);
        seen.set(`${step.kind}:${name}`, { kind: step.kind, name });
      }
    }
  }
  return [...seen.values()];
}

function summary(steps: ToolStep[], running: boolean): string {
  const edits = steps.filter((s) => s.kind === "edit").length;
  const n = steps.length;
  if (running) {
    const current = [...steps].reverse().find((s) => s.status === "running");
    return current?.label ?? `Arbetar … ${n} steg hittills`;
  }
  const parts = [`${n} steg`];
  if (edits) parts.push(`${edits} ${edits === 1 ? "ändring" : "ändringar"}`);
  return `Arbetade i ${parts.join(" · ")}`;
}

/**
 * What the agent did, as one quiet line that opens into a list. Closed by default once the
 * turn is over — the answer is what matters; the steps are there for whoever wants them.
 */
export function ActivitySteps({ steps, running }: { steps: ToolStep[]; running: boolean }) {
  const [open, setOpen] = useState(false);
  if (!steps.length) return null;
  const failed = steps.some((s) => s.status === "failed");
  const used = highlights(steps);

  return (
    <div className={styles.activity}>
      <button type="button" className={styles.toggle} aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className={styles.lead}>{running ? <Spinner /> : failed ? <XMarkIcon size={16} /> : <CheckIcon size={16} />}</span>
        <span className={styles.summary}>{summary(steps, running)}</span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}>
          <ChevronDownIcon size={16} />
        </span>
      </button>
      {/* Which recipes and helpers did the work — visible without opening the list. */}
      {used.length > 0 && (
        <ul className={styles.used} aria-label="Recept och hjälpagenter">
          {used.map((u) => (
            <li key={`${u.kind}:${u.name}`} className={styles.chip} title={u.kind === "skill" ? "Recept" : "Hjälpagent"}>
              <ToolIcon kind={u.kind}/>
              {u.name}
            </li>
          ))}
        </ul>
      )}
      {open && (
        <ol className={styles.list}>
          {steps.map((step) => (
            <li key={step.id} className={`${styles.step} ${step.parentId ? styles.nested : ""}`} data-status={step.status}>
              <span className={styles.kind}><ToolIcon kind={step.kind} /></span>
              <span className={styles.label}>
                {step.label}
                {step.detail && <code className={styles.detail}>{step.detail}</code>}
              </span>
              <span className={styles.state}>
                {step.status === "running" ? <Spinner /> : step.status === "failed" ? <XMarkIcon size={14} /> : <CheckIcon size={14} />}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
